import { useState } from "react";
import { useModal } from "../../components/organisms/modalProvider/ModalProvider";
import { ShowAlert } from "../../utils/showAlert";
import { UseGetStoreData } from "../getStoreData/UseGetStoreData";
import { sendPaymentViaLink, updateTicket, fetchAmbulance, updateAmbulanceInfo, createAmbulanceRequest } from "../../pages/actions/actions";
import { currentDateTime } from "../../utils/helper";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { getSingleBenData } from "../../http";

export const useExtraChargePaymentLink = ({ totalAmount }: { totalAmount: number }) => {
    const queryClient = useQueryClient();
    const dispatch = useDispatch();
    const [error, setError] = useState('');
    const [amount, setAmount] = useState<number>(totalAmount);
    const [number, setNumber] = useState<any>(null);
    const [email, setEmail] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedDetails, setSelectedDetails] = useState<any>([]);
    const { hideModal } = useModal();
    const { getTicketData } = UseGetStoreData();

    const buildBenList = () => {
        let benList = [];
        if (getTicketData?.patient) {
            benList.push({
                id: "patient",
                name: getTicketData?.patient?.fullName,
                mobile: getTicketData?.patient?.mobile,
                email: getTicketData?.patient?.email
            });
            if (getTicketData?.patient?.emergencyContacts) {
                getTicketData?.patient?.emergencyContacts.forEach((item: any, index: number) => {
                    benList.push({
                        id: `em-${index + 1}`,
                        name: item.fullName,
                        mobile: item.mobile
                    });
                });
            }
        }
        if (getTicketData?.poc) {
            benList.push({
                id: "poc",
                name: getTicketData?.poc?.fullName,
                mobile: getTicketData?.poc?.mobile
            });
        }
        return benList;
    };

    const benList = buildBenList();

    const handleAmountChange = (e: any) => {
        const value = e.target.value;
        setAmount(value ? Number(value) : 0);
    };

    const handleCollectMobileNumber = (e: any) => {
        let value = e.target.value;
        let formateOnlyDigits = value.replace(/\D/g, '');
        if (formateOnlyDigits && formateOnlyDigits <= 0) {
            ShowAlert("warning", "Please enter valid mobile number");
        }
        setNumber(formateOnlyDigits);
    };

    const handleCollectEmail = (e: any) => {
        const value = e.target.value;
        setEmail(value);
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(value)) {
            setError('');
        } else {
            setError('Invalid email format');
        }
    };

    const handleSelectChange = (value: any) => {
        const selectedItems = benList.filter((item) => value.includes(item.id));
        setSelectedDetails(selectedItems.length > 0 ? selectedItems : []);
    };

    const buildPayloadForPaymentLink = async () => {
        setIsLoading(true);
        let persistRoot = localStorage.getItem('persist:root') || '';
        let rootState = persistRoot ? JSON.parse(persistRoot) : {};
        let benificaryData = rootState?.beneficiary ? JSON.parse(rootState.beneficiary) : {};
        let benDatas = benificaryData?.benData ?? [];
        let benData = benDatas.find((data: any) => data?._id === getTicketData?.patient?.id);
        if (!benData && getTicketData?.patient?.id) {
            const singleBenData = await getSingleBenData(getTicketData?.patient?.id);
            benData = singleBenData?.data?.data;
        }

        const min = 1000000;
        const max = 20000000;
        let basePayload: any = {
            amount: amount,
            payment_status: "created",
            currency: "INR",
            notes: {
                order_by: "Extra Charges"
            },
            order_id: Math.floor(Math.random() * (max - min + 1)) + min.toString(),
            description: "Extra Charges",
            callback_url: "https://pococare.com",
            callback_method: "get",
            receipt: "Extra Charges",
            ticket_id: getTicketData?._id?.toString(),
            beneficiary: {},
            otherDetails: {
                ...((getTicketData?.subscriber?.id || benData?.subscriberId) && {
                    subscriberId: getTicketData?.subscriber?.id || benData?.subscriberId
                })
            },
        };

        if (selectedDetails.length) {
            const item = selectedDetails[0];
            basePayload.beneficiary = {
                name: item.name,
                mobile: item.mobile,
                ...(item.email && { email: item.email })
            };
        } else {
            basePayload.beneficiary = {
                name: getTicketData?.patient?.fullName,
                mobile: number
            };
            if (email) {
                basePayload.beneficiary.email = email;
            }
        }

        try {
            await sendPaymentViaLink(basePayload);
            const payload: any = {
                notes: [...getTicketData?.notes, {
                    date: currentDateTime(),
                    message: `PaymentLink Send for Extra Charges`
                }],
                ambulancePayment: { status: "created", amount: amount }
            };

            await updateTicket(getTicketData._id, payload, dispatch);
            await queryClient.invalidateQueries({
                queryKey: ['getOrdersByTicketId-extraCharges', getTicketData._id],
                exact: true,
                refetchType: 'all',
            });

            const response = await fetchAmbulance(getTicketData?._id);
            if (response?.length === 0) {
                await createAmbulanceRequest({
                    ticket_id: `${getTicketData?._id}`,
                    paymentStatus: "created",
                    amount: amount
                });
            } else {
                await updateAmbulanceInfo(getTicketData?._id, { 
                    paymentStatus: "created", 
                    // amount: amount 
                });
            }

            ShowAlert("success", "Payment link sent successfully");
            hideModal();
            setEmail(null);
            setNumber(null);
            setSelectedDetails([]);
            setIsLoading(false);
        } catch (e: any) {
            setEmail(null);
            setNumber(null);
            setSelectedDetails([]);
            setIsLoading(false);
            return ShowAlert("warning", e?.response?.data?.message[0] || e?.message || "Error sending payment link");
        }
    };

    const handleSendPaymentLink = async () => {
        if (!amount || amount <= 0) {
            return ShowAlert("warning", "Please enter valid amount");
        }
        if (!selectedDetails.length && !number && !email) {
            return ShowAlert("warning", "Please enter valid email or mobile or select beneficiary");
        }
        if (selectedDetails.length && (number || email)) {
            setEmail(null);
            setNumber(null);
            return ShowAlert("warning", "Either select beneficiary or enter email/mobile");
        }
        if (isLoading) {
            return ShowAlert("warning", "Please wait...");
        }
        await buildPayloadForPaymentLink();
    };

    return {
        error,
        number,
        email,
        isLoading,
        amount,
        benList,
        selectedDetails,
        handleAmountChange,
        handleCollectMobileNumber,
        handleCollectEmail,
        handleSelectChange,
        handleSendPaymentLink
    };
};
